import { observer, inject } from "mobx-react";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";
import { Button, Select, Text, TextArea, TextInput } from "../UI";
import { InnerBox } from "./Common";
import { useInput, inputsHasError } from "../utils/hooks";
import { isNotEmpty, withError } from "../validation";
import removeImg from "../assets/img/close-circle.svg";
import editImg from "../assets/img/edit-pencil.svg";
import { maxLength } from "../validation/length";
import PhotosWidget from "../components/PhotosWidget";
import { useMobile } from "../utils/mobile";

const TextItem = ({ value, label }) => {
  const theme = useTheme();
  return (
    <InnerBox
      style={{
        flexBasis: "35%",
      }}
    >
      <Text.SR
        color={theme.color.gray90}
        style={{
          textAlign: "start",
        }}
      >
        {label}
      </Text.SR>
      <Text.BR color={theme.color.gray90} style={{ textAlign: "start" }}>
        {value}
      </Text.BR>
    </InnerBox>
  );
};

const NewDefect = ({
  defect,
  handleDefectChange,
  validate,
  repairPagesStore,
  userStore
}) => {
  const { t } = useTranslation("applies");
  const [newDefect, setNewDefect] = useState({ ...defect });
  const [isEdit, setIsEdit] = useState(true);
  const [extFiles, setExtFiles] = useState([]);




  const APPCODE = "Bdk";

  const mustPic = userStore.applicationAttributes[APPCODE]?.Bdk_MustPic === "Y"

  const isMobile = useMobile();

  const areas =
    repairPagesStore.siteAreas?.map((area) => ({
      value: area.SITEAREADES,
      label: area.SITEAREADES,
    })) || [];

  const [area, setArea] = useState(
    newDefect?.SITEAREADES
      ? {
        value: newDefect?.SITEAREADES,
        label: newDefect?.SITEAREADES,
      }
      : null
  );

  const locations =
    repairPagesStore.locations?.map((location) => ({
      value: location.LOCATIONDES,
      label: location.LOCATIONDES,
    })) || [];

  const [location, setLocation] = useState(
    newDefect?.LOCATIONDES
      ? {
        value: newDefect?.LOCATIONDES,
        label: newDefect?.LOCATIONDES,
      }
      : null
  );

  const domains =
    repairPagesStore.domains?.map((domain) => ({
      value: domain.CHKPERIODTYPECODE,
      label: domain.CHKPERIODTYPEDES,
    })) || [];

  const [domain, setDomain] = useState(
    newDefect?.CHKPERIODTYPECODE
      ? {
        value: newDefect?.CHKPERIODTYPECODE,
        label: newDefect?.CHKPERIODTYPEDES,
      }
      : null
  );

  useEffect(() => {
    handleDefectChange(newDefect);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newDefect]);

  useEffect(() => {
    if (validate === true) validateDefectRow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validate]);

  useEffect(() => {
    const tempExts = [...extFiles];
    setNewDefect({
      ...newDefect,
      EXTFILENAME: tempExts?.shift()?.EXTFILENAME,
      ZCOB_DEFECTSLISTPIC_SUBFORM: tempExts,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extFiles]);

  const handleRemoveImg = (file) => {
    const filteredExtFiles = extFiles.filter(
      (item) => item.EXTFILENAME !== file.EXTFILENAME
    );
    setExtFiles(filteredExtFiles);
  };

  const isNotEmptyWithError = withError(
    isNotEmpty,
    t("text_field_empty_error")
  );

  const maxLengthWithError = withError(
    (input) => maxLength(input, 120),
    t("text_field_pwd_length_error")
  );

  const klineInput = useInput({
    validation: [isNotEmptyWithError],
    value: newDefect.KLINE,
  });

  const areaInput = useInput({
    label: t("area"),
    validation: [isNotEmptyWithError],
    placeholder: t("area_placeholder"),
    value: area || "",
  });

  const locationInput = useInput({
    label: t("location"),
    validation: [isNotEmptyWithError],
    placeholder: t("location_placeholder"),
    value: location || "",
  });

  const domainInput = useInput({
    label: t("domain"),
    validation: [isNotEmptyWithError],
    placeholder: t("domain_placeholder"),
    value: domain || "",
  });

  const subject = useInput({
    validation: [isNotEmptyWithError],
    placeholder: t("subject_placeholder"),
    value: newDefect?.DEFECTDES || "",
  });

  const description = useInput({
    validation: [isNotEmptyWithError, maxLengthWithError],
    placeholder: t("defect_description_placeholder"),
    value: !!newDefect.DEFECTDES
      ? `${!!newDefect.DEFECTDES ? newDefect.DEFECTDES : ``} ${!!newDefect?.ZCOB_DEFECTSLISTTEXT_SUBFORM?.TEXT
        ? newDefect?.ZCOB_DEFECTSLISTTEXT_SUBFORM?.TEXT
        : ``
      }`
      : ``,
  });

  const handleDescriptionChange = (value) => {
    setNewDefect({
      ...newDefect,
      DEFECTDES: value.substring(0, 68),
      ZCOB_DEFECTSLISTTEXT_SUBFORM: { TEXT: value.substring(68) },
    });
  };

  const handleAreaChange = (value) => {
    setArea(value);
    setNewDefect({ ...newDefect, SITEAREADES: value?.value });
  };

  const handleLocationChange = (value) => {
    setLocation(value);
    setNewDefect({ ...newDefect, LOCATIONDES: value?.value });
  };

  const handleDomainChange = (value) => {
    setDomain(value);
    setNewDefect({
      ...newDefect,
      CHKPERIODTYPECODE: value?.value,
      CHKPERIODTYPEDES: value?.label,
    });
  };

  const setREMOVE = () => {
    setNewDefect({ ...newDefect, REMOVE: true });
  };

  const setAPPROVE = (approve) => {
    setNewDefect({ ...newDefect, APPROVE: approve });
    setIsEdit(!approve);
  };

  const validateDefectRow = () => {
    let validFiles = true

    if (mustPic && extFiles.length === 0) {
      validFiles = false
    }

    const valid = !inputsHasError(
      domainInput,
      subject,
      locationInput,
      areaInput,
      klineInput,
      description
    );

    return valid && validFiles
  };

  return (
    <div style={{ width: "100%" }}>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "start",
          width: "100%",
          gap: isMobile ? 8 : 20,
        }}
      >
        {isEdit ? (
          <TextInput
            divStyle={{
              display: "flex",
              maxWidth: 150,
              height: 40,
              padding: "10px 0",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 12,
              flexBasis: "3%",
            }}
            {...klineInput}
            variant="outlined"
            label={t("kline")}
            error={!klineInput.error}
            helperText={klineInput.helperText}
            onChange={() => { }}
          ></TextInput>
        ) : (
          <TextItem value={newDefect.KLINE} label={t("kline")} />
        )}
        {!!isEdit ? (
          <Select
            {...areaInput}
            style={{
              textAlign: "right",
              minWidth: 150,
              height: 40,
              flexBasis: "35%",
            }}
            options={areas || []}
            onChange={(value) => handleAreaChange(value)}
            error={!areaInput.error}
            helperText={areaInput.helperText}
          ></Select>
        ) : (
          <TextItem value={newDefect.SITEAREADES} label={t("area")} />
        )}
        {!!isEdit ? (
          <Select
            {...locationInput}
            style={{
              textAlign: "right",
              minWidth: 150,
              height: 40,
              flexBasis: "35%",
            }}
            options={locations || []}
            onChange={(value) => handleLocationChange(value)}
            error={!locationInput.error}
            helperText={locationInput.helperText}
          ></Select>
        ) : (
          <TextItem value={newDefect.LOCATIONDES} label={t("location")} />
        )}
        {isEdit ? (
          <Select
            {...domainInput}
            style={{
              textAlign: "right",
              minWidth: 150,
              height: 40,
              flexBasis: "35%",
            }}
            options={domains || []}
            onChange={(value) => handleDomainChange(value)}
            error={!domainInput.error}
            helperText={domainInput.helperText}
          ></Select>
        ) : (
          <TextItem value={newDefect.CHKPERIODTYPEDES} label={t("domain")} />
        )}
        <div
          style={{
            display: "flex",
            gap: 20,
            flex: "1 1 0%",
            flexDirection: "row",
            flexWrap: "nowrap",
            justifyContent: "flex-end",
            alignItems: "center",
            alignSelf: "center",
            justifySelf: "flex-end",
            margin: "auto",
            flexBasis: "100%",
          }}
        >
          {!!isEdit ? (
            <Button
              style={{ marginTop: 0 }}
              onClick={() => setAPPROVE(true)}
              disabled={
                !newDefect.CHKPERIODTYPECODE ||
                !newDefect.LOCATIONDES ||
                !newDefect.SITEAREADES ||
                !newDefect.DEFECTDES || (mustPic && extFiles?.length === 0)
              }
            >
              {t("approve")}
            </Button>
          ) : (
            <div>
              <img src={editImg} alt="edit" onClick={() => setAPPROVE(false)} />
            </div>
          )}
          <div>
            <img src={removeImg} alt="del" onClick={setREMOVE}></img>
          </div>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: 12,
          marginTop: 20,
          alignItems: "start",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <div style={{ flexBasis: "50%" }}>
          {!!isEdit ? (
            <TextArea
              name="description"
              rows={4}
              cols={90}
              onBlur={(e) => handleDescriptionChange(e.target.value)}
              onChange={(e) => handleDescriptionChange(e.target.value)}
              {...description}
              variant="outlined"
              label={t("defect_description")}
              multiple={true}
              valid={!description.error}
              helperText={description.helperText}
            ></TextArea>
          ) : (
            <TextItem
              style={{ paddingTop: 0, flexBasis: "50%" }}
              value={
                !!newDefect.DEFECTDES
                  ? `${!!newDefect.DEFECTDES ? newDefect.DEFECTDES : ``} ${!!newDefect?.ZCOB_DEFECTSLISTTEXT_SUBFORM?.TEXT
                    ? newDefect?.ZCOB_DEFECTSLISTTEXT_SUBFORM?.TEXT
                    : ``
                  }`
                  : ``
              }
              label={t("defect_description")}
            />
          )}
        </div>
        <PhotosWidget
          limit={5}
          isEdit={isEdit}
          handleRemove={handleRemoveImg}
          photosList={extFiles}
          setPhotos={setExtFiles}
          valid={!!mustPic && !!validate ? extFiles.length > 0 : true}
        />
      </div>
    </div>
  );
};

export default inject(
  "userStore",
  "repairPagesStore",
)(observer(NewDefect));
